import api from '~/utils/api';
import { getSectionByPath, getSectionById, generateTitleFromPath, sectionMap } from './musicSectionMapConfig';
import type { SectionMapItem } from './musicSectionMapConfig';

// 定义生成音乐的类型
export interface GenMusic {
  id: string;
  title: string;
  metadata: {
    tags: string;
    prompt: string;
    duration?: number;
    gpt_description_prompt?: string;
  };
  audio_url: string;
  audio_oss?: string;
  stream_audio_url?: string;
  image_url: string;
  score: number;
  created_at: string;
  finish_time?: number;
}

export interface MusicTask {
  id: string;
  action: string;
  status: number;
  fail_reason: string;
  submit_time: number;
  start_time: number;
  finish_time: number;
  generate_time: number;
  task_time: number;
  progress: string;
  instrumental: boolean;
  music_data: GenMusic[];
  lyrics_data: {
    id: string;
    text: string;
    title: string;
  };
  owner: string;
  cert_time: number;
  cert_number: string;
  created: number;
} 

// 定义音乐榜单类型
export interface MusicRank {
  id: string;
  section_id: string;
  cover_url: string;
  count: number;
  created: number;
  selected_option: string;
  name: string;
  top_songs: any[];
}

// 定义音乐分类类型
export interface MusicCategory {
  id: string;
  image_url: string;
  search_tag: string;
  created: number;
  name: string;
}

// 定义音乐相关的类型
export interface Music {
  id: string;
  section_id?: string;
  title: string;
  video_url?: string;
  audio_url: string;
  image_url: string;
  image_large_url?: string;
  is_video_pending?: boolean;
  is_liked?: boolean;
  is_trashed?: boolean;
  is_public?: boolean;
  is_handle_updated?: boolean;
  handle?: string;
  user_id?: string;
  display_name?: string;
  avatar_image_url?: string;
  model_name?: string;
  major_model_version?: string;
  status?: string;
  play_count?: number;
  upvote_count?: number;
  metadata: {
    prompt: string;
    type?: string;
    duration?: number;
    has_vocal?: boolean;
    is_audio_upload_tos_accepted?: boolean;
    tags: string;
    task?: string;
  };
  created_at: string;
  created: number;
}

// 使用泛型定义响应类型
export interface MusicListResponse<T = Music> {
  data: T[] | {
    list?: T[];
    name?: string;
    total?: number;
    per_page?: number;
  };
  total?: number;
  page?: number;
  limit?: number;
}

export interface MusicQueryParams {
  page?: number;
  size?: number;
  genre?: string;
  search?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

// 歌词生成相关
export interface GenerateLyricsParams {
  prompt?: string;
  topics?: string;
  keyword?: string;
  image?: string;
  title?: string;
  tags?: string;
  moods?: string;
  style?: string;
  type?: string;
  songer_name?: string;  // Add any necessary properties for generating lyrics
}

export interface SearchResultParams {
  // 搜索关键词
  search: string;
  // 分页查询第几页，1 开始
  page: number;
  // 每页条数，最少 10 条，最多 100 条
  size: number;
}

// 本地存储键名
export const LYRIC_HISTORY_KEY = 'lyric_history_cache'
export const RAP_LYRIC_HISTORY_KEY = 'rap_lyric_history_cache'

// 人声分离相关接口
export interface VocalSeparationResponse {
  code: number;
  data: {
    result: {
      task_id: string;
    }
  };
  msg?: string;
}

export interface VocalSeparationParams {
  audio_url?: string;
  file_id?: string;
}

export interface VocalSeparationStatusResponse {
  code: number;
  data: {
    result: {
      task_id: string;
      status: number;
      message: string;
      instrumental_url?: string;
      vocals_url?: string;
    }
  };
  msg?: string;
}

// MV生成相关接口
export interface MusicVideoGenParams {
  task_id: string;
  music_id: string;
  images?: string[];
  size?: string;
}

export interface MusicVideoGenResponse {
  code: number;
  msg: string;
  data: {
    task_id: string;
  };
}

export interface MusicVideoResultParams {
  task_id: string;
}

export interface MusicVideoReq {
  audio: string;
  subtitle?: string;
  images?: string[];
  gen_images?: string[];
}

export interface MvInfo {
  id: string;
  user_id: string;
  user_type: number;
  languageid: number;
  req: MusicVideoReq;
  status: number;
  fail_reason?: string;
  start_time?: number;
  finish_time?: number;
  generate_time?: number;
  task_time?: number;
  // 标题
  title: string;
  // 视频url
  video_url?: string;
  // 封面url
  image_url?: string;
  // 创建时间，unix毫秒
  created: number;
}

export interface MusicVideoResultResponse {
  code: number;
  msg: string;
  data: MvInfo;
}

export interface MvListParams {
  page: number;
  size: number;
}

// 导出路径常量
export const API_PATHS_MUSIC = {
  VOCAL_SEPARATION: {
    STATUS: "music/v1/audio/status",
    STATUS_WITHOUT_AUTH: "/music/public/v1/audio/status",
    SPLIT: "/music/v1/audio/split",
    SPLIT_WITHOUT_AUTH: "/music/public/v1/audio/split"
  },
  UPLOAD: {
    FILE: "/music/v1/upload",
    FILE_WITHOUT_AUTH: "/music/public/v1/upload"
  },
  LYRICS: {
    GENERATE: "/music/v1/lyrics",
    GENERATE_WITHOUT_AUTH: "/music/public/v1/lyrics"
  },
  TASK: {
    RESULT: "/music/v1/result",
    RESULT_WITHOUT_AUTH: "/music/public/v1/result"
  },
  MV: {
    GEN: "/music/v1/mv/gen",
    RESULT: "/music/v1/mv/result"
  }
} as const;

// 音乐服务API
export const musicService = {
  // 获取订阅配置列表
  getSubscriptionConfig: (params?: any) => {
    return api.post<any>("music/v1/products", params);
  },
  // 获取积分历史记录
  getScoreHistory: (params?: any) => {
    return api.post<any>("music/v1/pointshis", params);
  },
  // 获取分享音乐信息
  getPublicShareMusic: (params: { token: string }) => {
    return api.post<any>("music/public/share", params);
  },

  // 获取用户积分
  getUserAccount: (params?: any) => {
    return api.post<any>("music/v1/status", params);
  },
  // 获取音乐榜单列表
  getFeaturedMusic: (params?: MusicQueryParams) => {
    return api.post<MusicListResponse<MusicRank>>("music/v1/trending", params);
  },

  // 获取更多榜单音乐数据
  getMoreChartMusic: (
    section_id?: string,
    selected_option?: string,
    params?: MusicQueryParams
  ) => {
    return api.post<MusicListResponse<Music>>("music/v1/trending/more", {
      section_id,
      selected_option,
      ...params,
    });
  },

  // 获取音乐分类列表
  getCategoryList: (params?: MusicQueryParams) => {
    return api.post<MusicListResponse<MusicCategory>>(
      "music/v1/category",
      params
    );
  },

  // 获取更多分类音乐数据
  getMoreCategoryMusic: (search_tag: string, params?: MusicQueryParams) => {
    return api.post<MusicListResponse<Music>>("music/v1/category/more", {
      search_tag,
      ...params,
    });
  },
  // 获取风格列表
  getStyleList: (params?: MusicQueryParams) => {
    return api.post<MusicListResponse<MusicCategory>>(
      "music/v1/styles",
      params
    );
  },
  // 获取Mood列表
  getMoodList: () => {
    return api.post<MusicListResponse<MusicCategory>>("music/v1/styles", {
      type: 1,
    });
  },
  // 随机生成prompt
  setRandomPrompt: (params?: any) => {
    return api.post<any>("music/v1/prompts", params);
  },
  // 生成歌词
  generateLyrics: (path: string, params: GenerateLyricsParams) => {
    return api.post<any>(path, params);
  },

  //轮询歌词/歌曲生成结果, 默认鉴权
  getMusicTaskResult: (
    params?: any,
    path: string = API_PATHS_MUSIC.TASK.RESULT
  ) => {
    return api.post<any>(path, params);
  },
  // 创建新音乐
  createMusic: (musicData: Partial<Music>) => {
    return api.post<Music>("/music/v1/music", musicData);
  },

  // 我创作的音乐列表
  getMyMusicList: (params?: any) => {
    return api.post<MusicListResponse<Music>>("music/v1/record/page", params);
  },

  // 获取歌词生成历史
  getLyricHistory: async (params?: any) => {
    // 获取远程数据
    return await api.post<MusicListResponse<Music>>("music/v1/record/page", {
      action: "LYRICS",
      ...params,
    });
  },

  // 上传文件
  uploadFile: (
    path: string,
    file: File,
    onProgress?: (progress: number) => void
  ) => {
    return api.uploadFile<any>(path, file, onProgress);
  },

  // 保存歌词生成记录到本地存储
  saveLyricHistory: (task: MusicTask, type: string = LYRIC_HISTORY_KEY) => {
    if (typeof window === "undefined") return;172.161721

    try {
      // 获取现有记录
      const existingHistory = localStorage.getItem(type);
      let history: MusicTask[] = [];

      if (existingHistory) {
        history = JSON.parse(existingHistory);
      }

      // 将新记录添加到开头
      history.unshift(task);

      // 限制存储最近的50条记录
      if (history.length > 50) {
        history = history.slice(0, 50);
      }

      // 保存回本地存储
      localStorage.setItem(type, JSON.stringify(history));
    } catch (error) {
      console.error("保存歌词历史记录失败:", error);
    }
  },

  // 从本地存储获取歌词生成记录
  getLocalLyricHistory: (type: string = LYRIC_HISTORY_KEY): MusicTask[] => {
    if (typeof window === "undefined") return [];

    try {
      const history = localStorage.getItem(type);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error("获取本地歌词历史记录失败:", error);
      return [];
    }
  },

  // 清除本地歌词生成记录
  clearLocalLyricHistory: (type: string = LYRIC_HISTORY_KEY) => {
    if (typeof window === "undefined") return;

    try {
      localStorage.removeItem(type);
    } catch (error) {
      console.error("清除本地歌词历史记录失败:", error);
    }
  },

  // 删除音乐记录或任务
  deleteRecord: (params: { category: number; ids: string[] }) => {
    return api.post<any>("music/v1/record/rm", params);
  },
  // 生成嘻哈名字
  generateRapName: (params?: any) => {
    return api.post<any>("music/v1/rapname/gen", params);
  },

  // 获取搜索热词
  getSearchHotWords: () => {
    return api.post<any>("music/v1/hotword");
  },

  // 获取搜索结果
  getSearchResult: (params: SearchResultParams) => {
    return api.post<MusicListResponse<Music>>("music/v1/search", params);
  },

  // 修改歌曲名称
  updateMusicTitle: (params?: any) => {
    return api.post<any>("music/v1/changetitle", params);
  },

  getPublicMusicDetail: (id: string) => {
    return api.post<any>("music/v1/get", { id });
  },

  // 开始人声分离
  startVocalSeparation: (path: string, params: VocalSeparationParams) => {
    return api.post<VocalSeparationResponse>(path, params);
  },

  // 获取人声分离状态
  getVocalSeparationStatus: (path: string, params: { task_id: string }) => {
    return api.post<VocalSeparationStatusResponse>(path, params);
  },

  // 生成MV
  generateMusicVideo: (params: MusicVideoGenParams) => {
    return api.post<MusicVideoGenResponse>(API_PATHS_MUSIC.MV.GEN, params);
  },

  // 获取MV生成结果
  getMusicVideoResult: (params: MusicVideoResultParams) => {
    return api.post<MusicVideoResultResponse>(
      API_PATHS_MUSIC.MV.RESULT,
      params
    );
  },

  // 我创作的MV列表
  getMvList: (params: MvListParams) => {
    return api.post<MusicListResponse<MvInfo>>("music/v1/mv/page", params);
  },
  // 生成分享链接
  generateShareLink: (params: { id: string }) => {
    return api.post<any>("/music/v1/genshare", params);
  },
};

export default musicService; 